import {
  getYear,
  isThisYear,
  isToday,
  isWithinInterval,
  isYesterday,
  subDays,
} from "date-fns";

import { NotesNoteFragment } from "@/generated/graphql";

/**
 *
 */
export interface Section {
  title: string;
  data: NotesNoteFragment[];
}

/**
 * Groups notes by date into categorized sections (Today, Yesterday, This Month, etc.)
 * @param notes - Array of note objects to group by creation date
 * @returns Array of sections with title and grouped notes data
 */
export default function groupNotesByDate(
  notes: NotesNoteFragment[]
): Section[] {
  const today = new Date();

  // Define Date Ranges
  // const yesterday = subDays(today, 1);
  // const pastTwoDays = subDays(today, 2);
  const pastMonth = subDays(today, 30);
  const lastMonthStart = subDays(today, 60);
  const pastSixMonths = subDays(today, 182);
  const lastYearStart = subDays(today, 364);

  // Helper function to categorize a single note
  const categorizeNote = (
    note: NotesNoteFragment
  ): [string, NotesNoteFragment] => {
    const createdAt = note.createdAt;

    if (isToday(createdAt)) {
      return ["Today", note];
    } else if (isYesterday(createdAt)) {
      return ["Yesterday", note];
    } else if (isWithinInterval(createdAt, { start: pastMonth, end: today })) {
      return ["This Month", note];
    } else if (
      isWithinInterval(createdAt, { start: lastMonthStart, end: pastMonth })
    ) {
      return ["Last Month", note];
    } else if (
      isWithinInterval(createdAt, { start: pastSixMonths, end: lastMonthStart })
    ) {
      return ["Last 6 Months", note];
    } else if (
      isThisYear(createdAt) &&
      isWithinInterval(createdAt, { start: lastYearStart, end: pastSixMonths })
    ) {
      return ["This Year", note];
    } else {
      const year = getYear(createdAt).toString();
      return [year, note];
    }
  };

  // Reduce notes into sections
  const groupedNotes = notes.reduce<Record<string, NotesNoteFragment[]>>(
    (acc, note) => {
      const [category, categorizedNote] = categorizeNote(note);
      return {
        ...acc,
        [category]: [...(acc[category] || []), categorizedNote],
      };
    },
    {}
  );

  // Convert the grouped notes into the required Section array format
  return (
    Object.entries(groupedNotes)
      // .slice(0)
      // .sort(([a], [b]) => (a > b ? -1 : 1)) // Sort by category title in descending order
      .map(([title, data]) => ({ title, data }))
  );
}
